import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import DinnerDiningOutlinedIcon from '@mui/icons-material/DinnerDiningOutlined';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import EventSeatOutlinedIcon from '@mui/icons-material/EventSeatOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupsIcon from '@mui/icons-material/Groups';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import moment from 'moment';
import { getHallBookings, getHallDetail } from '../modules/hall/api';
import HallAuditDrawer from '../modules/hall/components/HallAuditDrawer';
import HallDetailAnalytics from '../modules/hall/components/HallDetailAnalytics';
import HallEditDrawer from '../modules/hall/components/HallEditDrawer';
import HallModuleNav, { HALL_ACCENT } from '../modules/hall/components/HallModuleNav';
import { normalizeHall, getHallCardStatus } from '../modules/hall/utils/normalizeHall';
import { DataTable, FormDatePicker, FormSelect, FormTextField } from '../shared';
import { DASHBOARD_ACCENT, getDashboardDarkSurfaceTokens, getDashboardPageShellSx } from '../utils/dashboardSurfaceSx';

const numberFormat = new Intl.NumberFormat('en-IN');

const currencyFormat = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

function formatCurrency(value) {
  return currencyFormat.format(Number(value || 0));
}

function getStatusChipColor(status, isDark) {
  const key = String(status || '').toLowerCase();
  if (key === 'available' || key === 'active') {
    return { color: isDark ? '#bbf7d0' : '#166534', bg: alpha('#22c55e', isDark ? 0.22 : 0.14) };
  }
  if (key === 'occupied' || key === 'maintenance') {
    return { color: isDark ? '#fde68a' : '#92400e', bg: alpha('#f59e0b', isDark ? 0.22 : 0.14) };
  }
  if (key === 'confirmed' || key === 'completed') {
    return { color: isDark ? '#bbf7d0' : '#166534', bg: alpha('#22c55e', isDark ? 0.22 : 0.14) };
  }
  if (key === 'tentative') {
    return { color: isDark ? '#bfdbfe' : '#1d4ed8', bg: alpha('#3b82f6', isDark ? 0.22 : 0.14) };
  }
  if (key === 'cancelled' || key === 'inactive') {
    return { color: isDark ? '#fecaca' : '#991b1b', bg: alpha('#ef4444', isDark ? 0.22 : 0.14) };
  }
  return { color: isDark ? '#cbd5e1' : '#475569', bg: alpha('#64748b', isDark ? 0.2 : 0.12) };
}

function formatCapacityValue(value) {
  const num = Number(value);
  if (Number.isFinite(num) && num > 0) {
    return num.toLocaleString('en-IN');
  }
  return '—';
}

function HeaderStatTile({ icon: Icon, label, value, color, isDark }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        px: 1.15,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${alpha(color, isDark ? 0.28 : 0.2)}`,
        background: isDark
          ? `linear-gradient(160deg, ${alpha('#0f172a', 0.9)} 0%, ${alpha(color, 0.12)} 100%)`
          : `linear-gradient(160deg, #ffffff 0%, ${alpha(color, 0.1)} 100%)`,
      }}
    >
      <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mb: 0.35 }}>
        <Icon sx={{ fontSize: 15, color }} />
        <Typography
          variant="caption"
          sx={{
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 800,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.62rem',
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        sx={{
          color: isDark ? '#f8fafc' : '#0f172a',
          fontWeight: 900,
          fontSize: '1.05rem',
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

function eventStatusChipSx(status, isDark) {
  const key = String(status || '').toLowerCase();
  const colors = {
    confirmed: HALL_ACCENT,
    tentative: '#f6c453',
    completed: '#5b9cf5',
    cancelled: '#f07178',
  };
  const color = colors[key] || '#5b9cf5';
  return {
    height: 24,
    fontWeight: 800,
    textTransform: 'capitalize',
    color,
    bgcolor: alpha(color, isDark ? 0.16 : 0.12),
    border: `1px solid ${alpha(color, 0.28)}`,
  };
}

export default function HallDetailPage() {
  const { hallId } = useParams();
  const normalizedHallId = useMemo(() => {
    const parsed = Number(hallId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [hallId]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [analyticsStartDate, setAnalyticsStartDate] = useState(() => moment().startOf('month'));
  const [analyticsEndDate, setAnalyticsEndDate] = useState(() => moment().endOf('month'));
  const [analyticsPreset, setAnalyticsPreset] = useState('this_month');
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventRows, setEventRows] = useState([]);
  const [eventsCount, setEventsCount] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPerPage, setEventsPerPage] = useState(10);
  const [eventsFilters, setEventsFilters] = useState({
    event_id: '',
    search: '',
    date: '',
    status: '',
  });

  const showNotice = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const loadDetail = useCallback(async ({ showPageLoader = true } = {}) => {
    if (!normalizedHallId) {
      setError('Invalid hall ID');
      setLoading(false);
      return;
    }
    if (showPageLoader) setLoading(true);
    else setAnalyticsLoading(true);
    setError(null);
    try {
      const data = await getHallDetail(normalizedHallId, {
        analytics_start: analyticsStartDate?.format?.('YYYY-MM-DD') || moment().startOf('month').format('YYYY-MM-DD'),
        analytics_end: analyticsEndDate?.format?.('YYYY-MM-DD') || moment().endOf('month').format('YYYY-MM-DD'),
      });
      setPayload(data);
    } catch (err) {
      setError(err?.userMessage || err?.message || 'Failed to load hall details');
    } finally {
      if (showPageLoader) setLoading(false);
      setAnalyticsLoading(false);
    }
  }, [analyticsEndDate, analyticsStartDate, normalizedHallId]);

  const hasLoadedRef = useRef(false);
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [normalizedHallId]);
  useEffect(() => {
    const showPageLoader = !hasLoadedRef.current;
    loadDetail({ showPageLoader }).finally(() => {
      hasLoadedRef.current = true;
    });
  }, [loadDetail]);

  const handleAnalyticsDateRangeChange = useCallback((start, end, preset) => {
    if (start) setAnalyticsStartDate(start);
    if (end) setAnalyticsEndDate(end);
    setAnalyticsPreset(preset || 'custom');
  }, []);

  const analyticsRangeLabel = useMemo(() => {
    if (!analyticsStartDate || !analyticsEndDate) return 'Selected range';
    if (analyticsStartDate.isSame(analyticsEndDate, 'day')) {
      return analyticsStartDate.format('DD MMM YYYY');
    }
    return `${analyticsStartDate.format('DD MMM')} – ${analyticsEndDate.format('DD MMM YYYY')}`;
  }, [analyticsEndDate, analyticsStartDate]);

  const hall = useMemo(() => (payload?.hall ? normalizeHall(payload.hall) : null), [payload]);
  const stats = payload?.stats || {};
  const analytics = payload?.analytics || null;
  const recentActivity = Array.isArray(payload?.recent_activity) ? payload.recent_activity : [];

  const loadHallEvents = useCallback(async () => {
    if (!normalizedHallId) return;
    setEventsLoading(true);
    try {
      const data = await getHallBookings({
        hall_id: normalizedHallId,
        page: eventsPage,
        page_size: eventsPerPage,
        event_id: eventsFilters.event_id || undefined,
        search: eventsFilters.search || undefined,
        date: eventsFilters.date || undefined,
        status: eventsFilters.status || undefined,
      });
      setEventRows(data?.results || []);
      setEventsCount(Number(data?.count || 0));
    } catch (err) {
      showNotice('error', err?.userMessage || err?.message || 'Unable to load hall events.');
    } finally {
      setEventsLoading(false);
    }
  }, [
    eventsFilters.date,
    eventsFilters.event_id,
    eventsFilters.search,
    eventsFilters.status,
    eventsPage,
    eventsPerPage,
    normalizedHallId,
    showNotice,
  ]);

  useEffect(() => {
    setEventsPage(1);
  }, [normalizedHallId]);

  useEffect(() => {
    loadHallEvents();
  }, [loadHallEvents]);

  const handleEventsFilterChange = useCallback((field, value) => {
    setEventsFilters((prev) => ({ ...prev, [field]: value }));
    setEventsPage(1);
  }, []);

  const eventListColumns = useMemo(() => ([
    {
      field: 'event_id',
      label: 'Event ID',
      sortable: false,
      render: (_value, row) => {
        const eventId = row.event_id || row.booking_code || row.booking_id || `EVT-${row.id}`;
        return (
          <Typography
            component="span"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/hall/event/${row.id}/`);
            }}
            sx={{
              color: HALL_ACCENT,
              fontWeight: 800,
              fontSize: '0.82rem',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {eventId}
          </Typography>
        );
      },
    },
    {
      field: 'event_name',
      label: 'Event',
      sortable: false,
      render: (_value, row) => (
        <Stack spacing={0.3}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: 'text.primary' }}>
            {row.event_name || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {row.customer_name || row.customer || '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'event_date',
      label: 'Date',
      sortable: false,
      render: (_value, row) => {
        const dateValue = row.event_date || row.date;
        if (!dateValue) return '—';
        const parsed = moment(dateValue);
        return parsed.isValid() ? parsed.format('DD MMM YYYY') : dateValue;
      },
    },
    {
      field: 'time',
      label: 'Time',
      sortable: false,
      render: (_value, row) => (
        row.start_time && row.end_time
          ? `${row.start_time} - ${row.end_time}`
          : (row.time || '—')
      ),
    },
    {
      field: 'guests',
      label: 'Guests',
      align: 'right',
      sortable: false,
      render: (value) => numberFormat.format(Number(value || 0)),
    },
    {
      field: 'amount',
      label: 'Amount',
      align: 'right',
      sortable: false,
      render: (value) => formatCurrency(value || 0),
    },
    {
      field: 'status',
      label: 'Status',
      sortable: false,
      render: (value) => (
        <Chip
          size="small"
          label={value || 'tentative'}
          sx={eventStatusChipSx(value || 'tentative', isDark)}
        />
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      render: (_value, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/hall/event/${row.id}/`);
            }}
            sx={{ color: '#5b9cf5' }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/hall/event/${row.id}/`);
            }}
            sx={{ color: HALL_ACCENT }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ]), [isDark, navigate]);

  const eventListFilterRow = useMemo(() => eventListColumns.map((column) => {
    switch (column.field) {
      case 'event_id':
        return (
          <FormTextField
            key={column.field}
            label=""
            value={eventsFilters.event_id}
            onChange={(event) => handleEventsFilterChange('event_id', event.target.value)}
            placeholder="Event ID"
            sx={{ minWidth: 110 }}
          />
        );
      case 'event_name':
        return (
          <FormTextField
            key={column.field}
            label=""
            value={eventsFilters.search}
            onChange={(event) => handleEventsFilterChange('search', event.target.value)}
            placeholder="Search event / guest"
            sx={{ minWidth: 160 }}
          />
        );
      case 'event_date':
        return (
          <FormDatePicker
            key={column.field}
            label=""
            value={eventsFilters.date ? moment(eventsFilters.date, 'YYYY-MM-DD') : null}
            onChange={(value) => {
              handleEventsFilterChange(
                'date',
                value && value.isValid?.() ? value.format('YYYY-MM-DD') : '',
              );
            }}
            sx={{ minWidth: 140 }}
          />
        );
      case 'status':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={eventsFilters.status}
            onChange={(event) => handleEventsFilterChange('status', event.target.value)}
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="tentative">Tentative</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </FormSelect>
        );
      default:
        return <div key={column.field} />;
    }
  }), [eventListColumns, eventsFilters, handleEventsFilterChange]);

  const pageShellSx = getDashboardPageShellSx(isDark ? 'dark' : 'light');
  const surfaceTokens = getDashboardDarkSurfaceTokens(isDark);
  const accent = DASHBOARD_ACCENT;

  const modernPanelCardSx = {
    mb: 2,
    borderRadius: 2,
    border: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? surfaceTokens.surfaceGradient : '#ffffff',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    overflow: 'hidden',
  };

  const locationLine = [hall?.address, hall?.city, hall?.state, hall?.pincode, hall?.country]
    .filter(Boolean)
    .join(', ');

  if (loading) {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ mb: 2.5 }}>
          <HallModuleNav />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <CircularProgress sx={{ color: accent }} />
        </Box>
      </Box>
    );
  }

  if (error || !hall) {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ mb: 2.5 }}>
          <HallModuleNav />
        </Box>
        <Paper sx={{ ...modernPanelCardSx, p: 3, textAlign: 'center' }}>
          <Typography color="error">{error || 'Hall not found'}</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/hall/dashboard/')}>Back to Hall Dashboard</Button>
        </Paper>
      </Box>
    );
  }

  const hallDisplayStatus = getHallCardStatus(hall);
  const availabilityPalette = getStatusChipColor(hallDisplayStatus, isDark);
  const heroFade = isDark ? '#0b1220' : '#ffffff';
  const heroText = isDark ? '#f8fafc' : '#0f172a';
  const heroMuted = isDark ? '#94a3b8' : '#64748b';
  const heroSubtle = isDark ? '#cbd5e1' : '#475569';
  const heroCardBg = isDark ? alpha('#0b1220', 0.72) : alpha('#ffffff', 0.78);
  const heroCardBorder = isDark ? alpha('#fff', 0.14) : alpha('#0f172a', 0.1);

  return (
    <Box sx={{ ...pageShellSx, width: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ mb: 2.5 }}>
        <HallModuleNav />
      </Box>

      <Paper elevation={0} sx={{ ...modernPanelCardSx, mb: 3 }}>
        {/* Hero: full-bleed hall image with theme-aware transparent fade on the right */}
        <Box
          sx={{
            position: 'relative',
            minHeight: { xs: 520, md: 460 },
            overflow: 'hidden',
            background: heroFade,
          }}
        >
          {/* Full-bleed background image (shows through right fade) */}
          {hall.thumbnail ? (
            <Box
              component="img"
              src={hall.thumbnail}
              alt={hall.name}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                background: isDark
                  ? `linear-gradient(135deg, ${alpha(accent, 0.22)} 0%, #0b1220 100%)`
                  : `linear-gradient(135deg, ${alpha(accent, 0.28)} 0%, ${alpha('#e2e8f0', 0.9)} 100%)`,
              }}
            >
              <MapsHomeWorkIcon sx={{ fontSize: 88, color: accent, opacity: 0.7 }} />
            </Box>
          )}

          {/* Theme fade: left image clear → right translucent panel */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: {
                xs: isDark
                  ? `linear-gradient(180deg, ${alpha(heroFade, 0.15)} 20%, ${alpha(heroFade, 0.78)} 72%, ${alpha(heroFade, 0.9)} 100%)`
                  : `linear-gradient(180deg, ${alpha(heroFade, 0.08)} 20%, ${alpha(heroFade, 0.78)} 72%, ${alpha(heroFade, 0.9)} 100%)`,
                md: isDark
                  ? `linear-gradient(90deg, ${alpha(heroFade, 0.08)} 0%, ${alpha(heroFade, 0.2)} 38%, ${alpha(heroFade, 0.62)} 62%, ${alpha(heroFade, 0.82)} 100%)`
                  : `linear-gradient(90deg, ${alpha(heroFade, 0.02)} 0%, ${alpha(heroFade, 0.18)} 38%, ${alpha(heroFade, 0.68)} 62%, ${alpha(heroFade, 0.86)} 100%)`,
              },
            }}
          />

          <Chip
            size="small"
            label={hallDisplayStatus}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 3,
              fontWeight: 800,
              color: availabilityPalette.color,
              bgcolor: availabilityPalette.bg,
              border: `1px solid ${alpha('#fff', 0.45)}`,
              backdropFilter: 'blur(8px)',
              boxShadow: `0 6px 16px ${alpha('#000', 0.18)}`,
            }}
          />

          {/* Content grid over image */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              minHeight: { xs: 520, md: 460 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
            }}
          >
            <Box sx={{ display: { xs: 'none', md: 'block' } }} />

            <Box
              sx={{
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 2.25, md: 2.75 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1.75,
                background: {
                  xs: alpha(heroFade, isDark ? 0.72 : 0.78),
                  md: 'transparent',
                },
                backdropFilter: { xs: 'blur(10px)', md: 'none' },
              }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Chip
                  size="small"
                  label={hall.hall_code || '—'}
                  sx={{
                    fontWeight: 800,
                    color: '#04120e',
                    bgcolor: accent,
                  }}
                />
                <Chip
                  size="small"
                  label={`${hall.business_type_label || 'Hall'}${hall.hall_type ? ` · ${hall.hall_type}` : ''}`}
                  sx={{
                    fontWeight: 700,
                    color: heroText,
                    bgcolor: isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.08),
                    border: `1px solid ${heroCardBorder}`,
                    backdropFilter: 'blur(8px)',
                  }}
                />
                <Box sx={{ flex: 1 }} />
                <Chip
                  size="small"
                  icon={<FactCheckIcon sx={{ fontSize: '16px !important' }} />}
                  label={`Audit${recentActivity.length ? ` · ${recentActivity.length}` : ''}`}
                  onClick={() => setAuditDrawerOpen(true)}
                  sx={{
                    height: 32,
                    fontWeight: 800,
                    cursor: 'pointer',
                    color: isDark ? HALL_ACCENT : '#0f766e',
                    bgcolor: isDark ? alpha(HALL_ACCENT, 0.16) : alpha(HALL_ACCENT, 0.14),
                    border: `1px solid ${alpha(HALL_ACCENT, 0.4)}`,
                    backdropFilter: 'blur(8px)',
                    '& .MuiChip-icon': { color: 'inherit' },
                    '&:hover': {
                      bgcolor: isDark ? alpha(HALL_ACCENT, 0.24) : alpha(HALL_ACCENT, 0.22),
                    },
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setEditDrawerOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    color: '#04120e',
                    backgroundColor: HALL_ACCENT,
                    boxShadow: `0 8px 20px ${alpha(accent, 0.28)}`,
                    '&:hover': { backgroundColor: HALL_ACCENT, opacity: 0.94 },
                  }}
                >
                  Edit hall
                </Button>
              </Stack>

              <Box>
                <Typography
                  sx={{
                    color: heroText,
                    fontWeight: 950,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.1,
                    fontSize: { xs: '1.85rem', sm: '2.2rem', md: '2.35rem' },
                    textShadow: isDark
                      ? `0 2px 18px ${alpha('#000', 0.65)}`
                      : `0 1px 10px ${alpha('#fff', 0.85)}`,
                  }}
                >
                  {hall.name}
                </Typography>
                {locationLine ? (
                  <Stack direction="row" spacing={0.65} alignItems="flex-start" sx={{ mt: 0.85 }}>
                    <LocationOnIcon sx={{ fontSize: 18, color: '#f97316', mt: 0.15 }} />
                    <Typography sx={{ color: heroSubtle, fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.45 }}>
                      {locationLine}
                    </Typography>
                  </Stack>
                ) : null}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.1,
                }}
              >
                <Box
                  sx={{
                    p: 1.4,
                    borderRadius: 2.25,
                    bgcolor: heroCardBg,
                    border: `1px solid ${alpha(accent, isDark ? 0.4 : 0.35)}`,
                    backdropFilter: 'blur(12px)',
                    boxShadow: `0 10px 28px ${alpha('#000', isDark ? 0.28 : 0.08)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? accent : '#0f766e',
                      fontWeight: 900,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Hourly rate
                  </Typography>
                  <Typography sx={{ color: heroText, fontWeight: 950, fontSize: '1.45rem', mt: 0.25, lineHeight: 1.1 }}>
                    {formatCurrency(hall.pricing.hourly_rate)}
                  </Typography>
                  <Typography sx={{ color: heroMuted, fontWeight: 700, fontSize: '0.78rem', mt: 0.4 }}>
                    Full day {formatCurrency(hall.pricing.full_day_rate)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.4,
                    borderRadius: 2.25,
                    bgcolor: heroCardBg,
                    border: `1px solid ${alpha('#38bdf8', isDark ? 0.4 : 0.35)}`,
                    backdropFilter: 'blur(12px)',
                    boxShadow: `0 10px 28px ${alpha('#000', isDark ? 0.28 : 0.08)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#7dd3fc' : '#0369a1',
                      fontWeight: 900,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Main capacity
                  </Typography>
                  <Typography sx={{ color: heroText, fontWeight: 950, fontSize: '1.45rem', mt: 0.25, lineHeight: 1.1 }}>
                    {formatCapacityValue(hall.capacity)}
                  </Typography>
                  <Typography sx={{ color: heroMuted, fontWeight: 700, fontSize: '0.78rem', mt: 0.4 }}>
                    Today {stats.today_bookings || 0} · Floor {hall.floor || '—'}
                    {hall.floor_area ? ` · ${hall.floor_area}` : ''}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2.25,
                  bgcolor: heroCardBg,
                  border: `1px solid ${heroCardBorder}`,
                  backdropFilter: 'blur(12px)',
                  boxShadow: `0 10px 28px ${alpha('#000', isDark ? 0.28 : 0.08)}`,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(accent, isDark ? 0.22 : 0.18),
                      border: `1px solid ${alpha(accent, 0.45)}`,
                      flexShrink: 0,
                    }}
                  >
                    <PersonIcon sx={{ color: isDark ? accent : '#0f766e', fontSize: 24 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: heroMuted, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                    >
                      Hall contact
                    </Typography>
                    <Typography sx={{ color: heroText, fontWeight: 900, fontSize: '1.08rem', lineHeight: 1.2 }}>
                      {hall.manager_name || 'Not assigned'}
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
                      {hall.phone ? (
                        <Stack direction="row" spacing={0.45} alignItems="center">
                          <PhoneIcon sx={{ fontSize: 14, color: isDark ? accent : '#0f766e' }} />
                          <Typography sx={{ color: heroSubtle, fontWeight: 700, fontSize: '0.84rem' }}>
                            {hall.phone}
                          </Typography>
                        </Stack>
                      ) : null}
                      {hall.email ? (
                        <Stack direction="row" spacing={0.45} alignItems="center">
                          <EmailIcon sx={{ fontSize: 14, color: isDark ? '#c4b5fd' : '#7c3aed' }} />
                          <Typography
                            noWrap
                            sx={{ color: heroSubtle, fontWeight: 700, fontSize: '0.84rem', maxWidth: 220 }}
                          >
                            {hall.email}
                          </Typography>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 0.85,
                }}
              >
                {[
                  { label: 'Dining', value: formatCapacityValue(hall.dining_capacity), tint: accent },
                  { label: 'Banquet', value: formatCapacityValue(hall.banquet_style_capacity), tint: '#a78bfa' },
                  { label: 'Theatre', value: formatCapacityValue(hall.theatre_style_capacity), tint: '#f59e0b' },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      px: 1,
                      py: 0.9,
                      borderRadius: 1.75,
                      textAlign: 'center',
                      bgcolor: heroCardBg,
                      border: `1px solid ${alpha(item.tint, isDark ? 0.4 : 0.3)}`,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Typography sx={{ color: heroMuted, fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.06em' }}>
                      {item.label.toUpperCase()}
                    </Typography>
                    <Typography sx={{ color: heroText, fontWeight: 900, fontSize: '1.05rem', mt: 0.15 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Below-hero details */}
        <Box sx={{ p: { xs: 1.8, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {hall.description ? (
            <Typography
              variant="body2"
              sx={{
                color: isDark ? '#cbd5e1' : '#334155',
                lineHeight: 1.75,
                fontWeight: 500,
                maxWidth: 980,
              }}
            >
              {hall.description}
            </Typography>
          ) : null}

          <Box>
            <Typography
              variant="overline"
              sx={{
                color: accent,
                fontWeight: 900,
                letterSpacing: '0.1em',
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                mb: 1,
              }}
            >
              <GroupsIcon sx={{ fontSize: 16 }} />
              Capacity & layout
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(6, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              <HeaderStatTile
                icon={GroupsIcon}
                label="Main"
                value={formatCapacityValue(hall.capacity)}
                color={accent}
                isDark={isDark}
              />
              <HeaderStatTile
                icon={DinnerDiningOutlinedIcon}
                label="Dining"
                value={formatCapacityValue(hall.dining_capacity)}
                color="#38bdf8"
                isDark={isDark}
              />
              <HeaderStatTile
                icon={EventSeatOutlinedIcon}
                label="Banquet"
                value={formatCapacityValue(hall.banquet_style_capacity)}
                color="#a78bfa"
                isDark={isDark}
              />
              <HeaderStatTile
                icon={MeetingRoomOutlinedIcon}
                label="Theatre"
                value={formatCapacityValue(hall.theatre_style_capacity)}
                color="#f59e0b"
                isDark={isDark}
              />
              <HeaderStatTile
                icon={SquareFootOutlinedIcon}
                label="Floor area"
                value={hall.floor_area || '—'}
                color="#14b8a6"
                isDark={isDark}
              />
              <HeaderStatTile
                icon={LayersOutlinedIcon}
                label="Floor / level"
                value={hall.floor || '—'}
                color="#fb7185"
                isDark={isDark}
              />
            </Box>
          </Box>

          {hall.amenities?.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {hall.amenities.slice(0, 8).map((amenity) => (
                <Chip
                  key={amenity}
                  label={amenity}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    color: isDark ? '#e2e8f0' : '#0f172a',
                    bgcolor: alpha(accent, isDark ? 0.16 : 0.14),
                    border: `1px solid ${alpha(accent, 0.28)}`,
                  }}
                />
              ))}
              {hall.amenities.length > 8 ? (
                <Chip
                  size="small"
                  label={`+${hall.amenities.length - 8} more`}
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    color: isDark ? '#94a3b8' : '#64748b',
                    bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.05),
                  }}
                />
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </Paper>

      <HallDetailAnalytics
        hallName={hall.name}
        analytics={analytics}
        isDark={isDark}
        themeMode={isDark ? 'dark' : 'light'}
        formatCurrency={formatCurrency}
        rangeLabel={analyticsRangeLabel}
        startDate={analyticsStartDate}
        endDate={analyticsEndDate}
        preset={analyticsPreset}
        onDateRangeChange={handleAnalyticsDateRangeChange}
        onRefresh={() => loadDetail({ showPageLoader: false })}
        loading={analyticsLoading}
        surfaceSx={modernPanelCardSx}
      />

      <Paper elevation={0} sx={{ ...modernPanelCardSx, p: { xs: 1.8, sm: 2.25 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a', letterSpacing: '-0.01em' }}
            >
              Event List
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
              All events for {hall.name} with filters and pagination.
            </Typography>
          </Box>

          <DataTable
            columns={eventListColumns}
            data={eventRows}
            loading={eventsLoading}
            pagination={{
              page: Math.max(eventsPage - 1, 0),
              perPage: eventsPerPage,
              totalRecords: eventsCount,
            }}
            onPageChange={(page) => setEventsPage(page + 1)}
            onPerPageChange={(perPage) => {
              setEventsPerPage(Number(perPage || 10));
              setEventsPage(1);
            }}
            emptyMessage="No events found for this hall."
            stickyHeader
            filterRow={eventListFilterRow}
            onRowClick={(row) => navigate(`/hall/event/${row.id}/`)}
          />
        </Stack>
      </Paper>

      <HallAuditDrawer
        open={auditDrawerOpen}
        onClose={() => setAuditDrawerOpen(false)}
        hallName={hall.name}
        hallCode={hall.hall_code}
        events={recentActivity}
        isDark={isDark}
        loading={analyticsLoading}
        onRefresh={() => loadDetail({ showPageLoader: false })}
      />

      <HallEditDrawer
        open={editDrawerOpen}
        hall={hall}
        isDark={isDark}
        onClose={() => setEditDrawerOpen(false)}
        onSaved={() => {
          loadDetail();
        }}
        onNotice={showNotice}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
